import { FC, useState } from 'react';
import { ICaremapNoteResponse } from '../model/NotesClient';
import { IExtractedSupportEntry, IRecording, RecordingState } from '../../recording/RecordingState';
import { IKeyWithMembers } from '../../caremap/ICareMap';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../../caremap/CareMapState';
import { HistoryViewModel } from '../../support/HistoryViewModel';
import { CareMapDrawerRouter } from '../../caremap/CareMapDrawerRouter';
import { useNudgeStorage } from '../../utils/useNudgeStorage';
import { SelectableText } from '../../components/SelectableText';
import { IonButton, IonIcon, IonPopover } from '@ionic/react';
import { close, informationCircleOutline } from 'ionicons/icons';
import { createUseStyles } from 'react-jss';
import { DuoUserIcon, DuoClipboardListIcon, DuoHandDotsIcon, DuoPrescriptionBottleIcon, DuoLocationDotIcon, DuoCalendarDaysIcon, DuoAddressCardIcon } from '../../theme/Icons';
import { BeeChatViewModel } from '../../helper-bee/BeeChatViewModel';
import { IconComponent } from '../../theme/Icons';
import { hasExtractedFields, DISPLAYED_FIELD_KEYS, IDisplayedFieldKey } from './hasExtractedFields';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';
import { NewCareMapWizard } from '../../wizard/mini-wizard/wizards/NewCareMapWizard';
import dayjs from 'dayjs';

// Labels/icons per displayed key; deriving FIELD_CONFIG from DISPLAYED_FIELD_KEYS (the
// visibility list) makes it a compile error for the two to drift apart.
const FIELD_META: Record<IDisplayedFieldKey, { label: string; Icon: IconComponent }> = {
    careRecipientName: { label: 'Care Recipient', Icon: DuoUserIcon },
    relationship: { label: 'Your Relationship', Icon: DuoUserIcon },
    diagnoses: { label: 'Diagnoses', Icon: DuoClipboardListIcon },
    allergies: { label: 'Allergies', Icon: DuoHandDotsIcon },
    medications: { label: 'Medications', Icon: DuoPrescriptionBottleIcon },
    address: { label: 'Location', Icon: DuoLocationDotIcon },
    supportsAdded: { label: 'Care Circle', Icon: DuoUserIcon },
    supportsUpdated: { label: 'Care Circle Updates', Icon: DuoUserIcon },
    dob: { label: 'Birthdate', Icon: DuoCalendarDaysIcon },
    contactInfo: { label: 'Contact Info', Icon: DuoAddressCardIcon },
};

const FIELD_CONFIG = DISPLAYED_FIELD_KEYS.map(key => ({ key, ...FIELD_META[key] }));

const formatFieldValue = (value: string | string[] | undefined): string | undefined => {
    if (!value) {
        return undefined;
    }
    if (Array.isArray(value)) {
        return value.length > 0 ? value.join(', ') : undefined;
    }
    return value;
};

const useStyles = createUseStyles({
    '@keyframes hide': {
        from: { opacity: 1 },
        to: { opacity: 0 }
    },

    hiding: {
        animation: '$hide ease 0.5s',
        opacity: 0
    },

    container: {
        margin: '20px 0',
    },

    bubble: {
        background: 'rgba(var(--ion-color-primary-rgb), 0.1)',
        borderRadius: 16,
        padding: '12px 16px',
        position: 'relative' as const,
    },

    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginBottom: 10,
    },

    avatar: {
        width: 32,
        height: 32,
        borderRadius: '50%',
        objectFit: 'cover' as const,
        flexShrink: 0,
    },

    headerInfo: {
        flex: 1,
    },

    name: {
        fontSize: 14,
        fontWeight: 600,
        color: 'var(--ion-text-color)',
    },

    closeButton: {
        alignSelf: 'flex-start',
        '--padding-start': '2px',
        '--padding-end': '2px',
    },

    commentText: {
        fontSize: 14,
        lineHeight: '1.5',
        color: 'var(--ion-color-step-900)',
        marginBottom: 14,
    },

    fieldsHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 14,
        marginBottom: 10,
    },

    fieldsTitleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
    },

    fieldsTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: 'var(--ion-text-color)',
    },

    infoIcon: {
        color: 'var(--ion-color-step-500)',
        fontSize: 16,
        cursor: 'pointer',
    },

    infoPopover: {
        '--width': '260px',
    },

    infoPopoverContent: {
        padding: '12px 14px',
        fontSize: 13,
        lineHeight: '1.5',
        color: 'var(--ion-text-color)',
    },

    fieldsCard: {
        background: 'white',
        borderRadius: 8,
        padding: '8px 12px',
    },

    fieldRow: {
        display: 'flex',
        // Top-aligned so a label sits level with the first line of any multiline value.
        alignItems: 'flex-start',
        gap: 8,
        padding: '6px 0',
        borderBottom: '1px solid var(--ion-color-step-100)',
        '&:last-child': {
            borderBottom: 'none',
        },
    },

    fieldIcon: {
        width: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        textAlign: 'center' as const,
    },

    fieldLabel: {
        fontSize: 13,
        color: 'var(--ion-color-step-600)',
        fontWeight: 400,
        width: 100,
        flexShrink: 0,
    },

    fieldValue: {
        fontSize: 13,
        color: 'var(--ion-text-color)',
        fontWeight: 600,
        flex: 1,
        textAlign: 'right' as const,
    },

    supportLink: {
        color: 'var(--ion-color-primary)',
        cursor: 'pointer',
    },

    profileLink: {
        fontSize: 14,
        fontWeight: 500,
        color: 'var(--ion-color-primary)',
        cursor: 'pointer',
    },

    buttons: {
        marginTop: 14,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'center',
        '& ion-button': {
            flex: '1 1 auto',
            minWidth: 140,
        },
    },
});

type IBeaCommentBubbleProps = {
    note: ICaremapNoteResponse;
    onCloseDrawer: () => void;
};

export const BeaCommentBubble: FC<IBeaCommentBubbleProps> = ({ note, onCloseDrawer }) => {
    const classes = useStyles();
    const careMapState = useInstance(CareMapState);
    const history = useInstance(HistoryViewModel);
    const drawerRouter = useInstance(CareMapDrawerRouter);
    const beeChatViewModel = useInstance(BeeChatViewModel);
    const tracking = useInstance(MixPanelEventEmitter);
    const recordingState = useInstance(RecordingState);
    const newCareMapWizard = useInstance(NewCareMapWizard);

    const [hiding, setHiding] = useState(false);

    const [beaCommentVisible, dismissBeaComment] = useNudgeStorage({ nudgeId: `bea-comment-${note.noteId}` });

    const recording = note.otherAttributes?.recording as IRecording | undefined;
    const beaComment = recording?.summary?.beaComment;
    const extractedFields = recording?.extractedFields;
    const hasFields = hasExtractedFields(extractedFields);
    const name = extractedFields?.careRecipientName || careMapState.getPatientFirstName() || undefined;

    // Placeholder is only relevant for intro recordings where the user is redirected
    // to the note view while processing is still in progress
    const isProcessing = !!recording && recording.summaryPrompt === 'intro'
        && recordingState.isAwaitingAIProcessing(recording);

    const isIntro = recording?.summaryPrompt === 'intro';
    const essentialMapComplete = careMapState.getActiveCaremap()?.essentialMapComplete ?? false;
    // Insufficient extraction: an intro recording finished processing but didn't yield enough
    // fields to start a profile. Shows retry + type-it instead of the generic Chat About This
    // affordance, regardless of whether the API supplied a fallback beaComment.
    const isInsufficientExtraction = isIntro && !essentialMapComplete && !hasFields;

    // A finished insufficient intro must render even when the model also failed to supply a
    // fallback beaComment — the retry/type-it guidance below IS the content.
    const hasRealContent = !!beaComment || hasFields || (isInsufficientExtraction && recording?.status === 'summarized');
    const showPlaceholder = isProcessing && beaCommentVisible;
    const showBubble = !isProcessing && hasRealContent && beaCommentVisible;

    if (!showPlaceholder && !showBubble) {
        return null;
    }

    const onDismiss = () => {
        setHiding(true);
        setTimeout(() => {
            setHiding(false);
            dismissBeaComment();
        }, 500);
    };

    const trackProfileLinkOpened = (linkType: 'View Profile' | 'Add More Info') => {
        tracking.track('Bea Comment Profile Link Opened', () => ({
            'Link Type': linkType,
            'Note Id': note.noteId,
            'Summary Prompt': recording?.summaryPrompt,
            'Extracted Fields': FIELD_CONFIG
                // Supports entries are objects rather than strings, so check presence directly
                // instead of via formatFieldValue (which only accepts string | string[]).
                .filter(({ key }) => {
                    const value = extractedFields?.[key];
                    return Array.isArray(value) ? value.length > 0 : !!value;
                })
                .map(({ label }) => label),
        }));
    };

    const onViewProfile = () => {
        trackProfileLinkOpened('View Profile');
        const careMapId = careMapState.getActiveCaremap()?.careMapId;
        history.push(`/care/${careMapId}`);
    };

    const onAddMoreInfo = () => {
        trackProfileLinkOpened('Add More Info');
        // Add More Info routes directly into phase 2 of the wizard, bypassing
        // NewCareMapWizard.start(), so we fire the questionnaire-started event here.
        tracking.track('Add Care Recipient Questionnaire Started', () => ({
            'Started From': 'add-more-info'
        }));
        const careMapId = careMapState.getActiveCaremap()?.careMapId;
        const redirectTo = encodeURIComponent(`/care/${careMapId}`);
        history.push(`/q/new-care-recipient-2?id=${careMapId}&startFromBeginning=true&redirectTo=${redirectTo}`);
    };

    const onChatWithBea = () => {
        // beaComment can be absent when the bubble renders for extracted fields alone
        const initialMessage = beaComment
            ? `Tell me more about this comment you left on my note "${note.title}": ${beaComment}`
            : `Tell me more about what you noticed in my note "${note.title}"`;
        beeChatViewModel.open({ trackingFrom: 'Bea Comment', initialMessage });
    };

    const onTryRecordingAgain = () => {
        // Close the note drawer first — the recording sheet is mounted at the
        // app shell and won't be visible until the drawer dismisses.
        onCloseDrawer();
        recordingState.triggerRecording({
            initiatedFrom: 'Add Care Recipient Retry',
            summaryPrompt: 'intro'
        });
    };

    const onTypeItInstead = () => {
        onCloseDrawer();
        newCareMapWizard.start(undefined, { trackingFrom: 'type-it-instead' });
    };

    // Partial profile: an intro recording yielded some fields but the
    // essential profile is not yet complete. The "Add More Info" link in
    // the captured-fields header is the right next step; suppress the
    // Chat About This button so it doesn't compete for attention.
    const isPartialProfile = isIntro
        && !essentialMapComplete
        && hasFields;

    // Placeholder: shown while processing is still in progress
    if (showPlaceholder) {
        return (
            <div className={classes.container} data-type="bea-comment-bubble">
                <div className={classes.bubble}>
                    <div className={classes.header}>
                        <img
                            src="/assets/images/help/helper-bea.png"
                            alt="Bea"
                            className={classes.avatar}
                        />
                        <div className={classes.headerInfo}>
                            <div className={classes.name}>Bea</div>
                        </div>
                    </div>
                    <div className={classes.commentText}>
                        {"I'm still reviewing what you shared, check back in a moment!"}
                    </div>
                </div>
            </div>
        );
    }

    // This branch deliberately supersedes whatever the model wrote. A silent intro still comes back
    // with a warm beaComment from the API, but nothing was extracted, so a welcome would leave the
    // user with no idea why their care profile is empty or what to do next. The override is
    // invisible from the API side, so read this before describing what a user sees after an intro.
    const commentText = isInsufficientExtraction
        ? `I didn't catch enough to start a profile yet. Try again with a bit more detail, or enter the basics yourself. Once we have a profile set up, I can record and summarize appointments, send updates to family, and get everything organized.`
        : beaComment
            || (hasFields
                ? `I picked up a few things from your intro that I've added to ${name || 'the care recipient'}'s profile. Take a look and let me know if anything needs updating!`
                : `Welcome to Innerhive! When you're ready, you can bring Bea along to record appointments and get automatic summaries, or set up a Care Recipient Profile to keep everything organized.`);

    const groupLabels = careMapState.getAllSupportGroupLabels();
    // Category titles must match what the care map itself calls each group.
    const supportCategoryTitle = (category: IKeyWithMembers): string | undefined =>
        category === 'household' ? 'Inner Circle' : groupLabels[category]?.title;

    const trackMemberLinkOpened = (category: IKeyWithMembers) => {
        tracking.track('Bea Comment Member Link Opened', () => ({
            'Member Category': category,
            'Note Id': note.noteId,
            'Summary Prompt': recording?.summaryPrompt
        }));
    };

    // Extraction snapshots outlive members: a linked member may since have been deleted from
    // the care map, so only render a link when the id still resolves in the entry's group.
    const memberStillExists = (entry: IExtractedSupportEntry): boolean => {
        const careMap = careMapState.getActiveCaremap();
        if (!careMap || !entry.id) {
            return false;
        }
        // entry.category arrives as untyped JSON off the note record — an unrecognized key must
        // degrade to "no link", never crash the note view.
        const members = entry.category === 'household' ? careMap.household.members : careMap[entry.category]?.members;
        return members?.some(member => member.id === entry.id) ?? false;
    };

    const onMemberLinkClick = (entry: IExtractedSupportEntry) => {
        trackMemberLinkOpened(entry.category);
        const careMapId = careMapState.getActiveCaremap()?.careMapId;
        const basePath = entry.category === 'household'
            ? `/caremaps/${careMapId}/drawer/household`
            : `/caremaps/${careMapId}/drawer/support-group/${entry.category}`;
        // Never push the member path directly: opening the drawer from outside it requires the
        // base-then-member push sequence (CareMapDrawerRouter's not-in-the-drawer branch) — a raw
        // deep push updates the URL and the map's selection without ever presenting the drawer.
        drawerRouter.navigateToSelectedMemberId({ basePath, memberId: entry.id });
    };

    type IVisibleField = { fieldKey: IDisplayedFieldKey; label: string; Icon: IconComponent; value?: string; supports?: IExtractedSupportEntry[] };
    const visibleFields: IVisibleField[] = hasFields
        ? FIELD_CONFIG
            .map(({ key, label, Icon }): IVisibleField | null => {
                if (key === 'supportsAdded' || key === 'supportsUpdated') {
                    const supports = extractedFields![key];
                    return supports?.length ? { fieldKey: key, label, Icon, supports } : null;
                }
                // dob is LLM-extracted free text; an unparseable value ("in the 1950s") must render
                // as-is, not as dayjs's literal "Invalid Date".
                const raw = key === 'dob' && extractedFields!.dob && dayjs(extractedFields!.dob).isValid()
                    ? dayjs(extractedFields!.dob).format('MM/DD/YYYY')
                    : extractedFields![key];
                const formatted = formatFieldValue(raw);
                return formatted ? { fieldKey: key, label, Icon, value: formatted } : null;
            })
            .filter((field): field is IVisibleField => field !== null)
        : [];

    return (
        <div className={`${classes.container} ${hiding ? classes.hiding : ''}`} data-type="bea-comment-bubble">
            <div className={classes.bubble}>
                <div className={classes.header}>
                    <img
                        src="/assets/images/help/helper-bea.png"
                        alt="Bea"
                        className={classes.avatar}
                    />
                    <div className={classes.headerInfo}>
                        <div className={classes.name}>Bea</div>
                    </div>
                    <IonButton
                        color="medium"
                        fill="clear"
                        shape="round"
                        size="small"
                        onClick={onDismiss}
                        className={classes.closeButton}
                        data-id="bea-comment-close"
                    >
                        <IonIcon slot="icon-only" icon={close} />
                    </IonButton>
                </div>

                <div className={`${classes.commentText} mp-mask`}>
                    <SelectableText>{commentText}</SelectableText>
                </div>

                {visibleFields.length > 0 && (<>
                    <div className={classes.fieldsHeader}>
                        <div className={classes.fieldsTitleRow}>
                            <div className={classes.fieldsTitle}>{"Here's what I captured"}</div>
                            <IonIcon
                                icon={informationCircleOutline}
                                className={classes.infoIcon}
                                id={`bea-captured-info-${note.noteId}`}
                                data-id="bea-captured-info"
                            />
                            <IonPopover
                                trigger={`bea-captured-info-${note.noteId}`}
                                triggerAction="click"
                                className={classes.infoPopover}
                            >
                                <div className={classes.infoPopoverContent}>
                                    These are details Bea picked up from this recording and saved
                                    to the Care Profile. You can view or update the latest info
                                    from the Care Profile.
                                </div>
                            </IonPopover>
                        </div>
                        {isIntro && !essentialMapComplete ? (
                            <div className={classes.profileLink} onClick={onAddMoreInfo} data-type="edit-profile">
                                Add More Info
                            </div>
                        ) : (
                            <div className={classes.profileLink} onClick={onViewProfile} data-type="view-profile">
                                View Profile
                            </div>
                        )}
                    </div>
                    <div className={classes.fieldsCard}>
                        {visibleFields.map(({ fieldKey, label, value, supports, Icon }) => (
                            <div key={label} className={classes.fieldRow} data-id={`bea-field-${fieldKey}`}>
                                <div className={classes.fieldIcon}>
                                    <Icon color="medium" size="small" />
                                </div>
                                <span className={classes.fieldLabel}>{label}</span>
                                {supports ? (
                                    <div className={`${classes.fieldValue} mp-mask`}>
                                        {supports.map((entry, index) => {
                                            const entryLabel = entry.name || entry.role;
                                            const title = supportCategoryTitle(entry.category);
                                            return (
                                                <div key={entry.id ?? `${entry.category}-${index}`} data-id="bea-support-entry">
                                                    {entryLabel && memberStillExists(entry) ? (
                                                        <span
                                                            className={classes.supportLink}
                                                            onClick={() => onMemberLinkClick(entry)}
                                                            data-id="bea-support-link"
                                                        >
                                                            {entryLabel}
                                                        </span>
                                                    ) : (
                                                        entryLabel || title
                                                    )}
                                                    {/* Non-breaking spaces keep a multi-word category ("Primary Care") wrapping as one unit. */}
                                                    {entryLabel && title ? ` (${title.replace(/ /g, '\u00A0')})` : ''}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <span className={`${classes.fieldValue} mp-mask`}>{value}</span>
                                )}
                            </div>
                        ))}
                    </div>
                </>)}

                {!isPartialProfile && (
                    <div className={classes.buttons}>
                        {isInsufficientExtraction ? (
                            <>
                                <IonButton
                                    color="primary"
                                    onClick={onTryRecordingAgain}
                                    data-id="bea-comment-try-recording-again"
                                >
                                    Try Recording Again
                                </IonButton>
                                <IonButton
                                    color="primary"
                                    fill="outline"
                                    onClick={onTypeItInstead}
                                    data-id="bea-comment-type-instead"
                                >
                                    Type It Instead
                                </IonButton>
                            </>
                        ) : (
                            <IonButton
                                color="primary"
                                fill="outline"
                                onClick={onChatWithBea}
                                data-id="bea-comment-chat"
                            >
                                Chat About This
                            </IonButton>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
